import { Progress } from 'antd'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { LoadingSpinner } from '../LoadingSpinner'
import { AsyncPhotoData } from './AsyncPhotoUpload'

type FetchedLoadingImage = {
  cdnUrl: string
  fromJob?: boolean
}

type UploadedLoadingImage = FetchedLoadingImage & AsyncPhotoData

export type LoadingImageData = FetchedLoadingImage | UploadedLoadingImage

export const isUploadedPhoto = (photo: LoadingImageData): photo is UploadedLoadingImage =>
  !!(photo as UploadedLoadingImage).filename

export const isPhotoUploading = (photo: LoadingImageData): boolean => {
  if (!isUploadedPhoto(photo)) {
    return false
  }
  return !photo.recordWritten
}

type LoadingImageProps = {
  image: LoadingImageData
  className?: string
  width: number
  height?: number
  style?: React.CSSProperties
  onClick?: () => void
}

// TODO: refactor `targets/web-app/src/pages/EstimatesFlow/components/Gallery.tsx` to use this
export const LoadingImage = React.memo<LoadingImageProps>(({ image, className, style, onClick, width, height }) => {
  const src = useMemo(() => (isUploadedPhoto(image) ? image.base64File : image.cdnUrl), [image])
  return (
    <div
      className={classNames('relative min-w-0 overflow-hidden', className)}
      style={{
        width: `${width}px`,
        height: height ? `${height}px` : undefined,
        ...style,
      }}
      onClick={onClick}
    >
      <img
        src={src}
        alt={image.cdnUrl}
        className="rounded object-cover"
        style={{
          width: `${width}px`,
          height: height ? `${height}px` : undefined,
        }}
      />
      {/* We show a loading spinner if the photo isn't loaded. There are three stages: 1. getting the S3
          url, 2, uploading the image, and 3. updating our DB. In normal circumstances steps 1 and 3 are
          instantaneous. But on really slow internets they take time. During step 2 we get progress
          percentages so we can render the circle that fills in. During the other two we show a normal
          loading spinner. We know step 1 is over when photo.progress > 0 (meaning step 2 has started). We
          know step 2 is over when `photo.progress === 1`, and we know step 3 is done when
          `photo.recordWritten` is true. Since `photo.recordWritten` indicates the end of everything, that's
          what tells us if we're at any stage in the process, so show this loader if that is false. */}
      {isUploadedPhoto(image) && !image.recordWritten && (
        <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm">
          {/* We show the normal spinner in stages 1 and 3. We know it's stage 1 if `photo.progress === 0`.
              We know it's stage 3 when `photo.progress === 1`. So in either of those cases we show the
              loading spinner. Otherwise we know we're in stage 2 and we show the progress circle. */}
          {image.progress === 0 || image.progress === 1 ? (
            <LoadingSpinner />
          ) : (
            <Progress
              type="circle"
              showInfo={false}
              percent={image.progress * 100}
              // The loading spinner uses font awesome icons. This makes the circle with an SVG. I had to do
              // "-3" to get it to look like the same size.
              size={width / 2 - 3}
              trailColor="#D9D9D9"
              strokeColor="#69c0ff"
              // This also came from eye-balling the regular spinner
              strokeWidth={10}
            />
          )}
        </div>
      )}
    </div>
  )
})

LoadingImage.displayName = 'LoadingImage'
